import { BACKEND } from "./api";

const URL_TRACK = `${BACKEND}/api/track`;
const SESSION_GAP = 30 * 60 * 1000;
const IDLE_MS = 30000;
const S = { on: false, visited: false, lang: "en", activeCat: null, queue: [], seen: new Set(), cats: new Set(), depth: 0, lastInput: Date.now(), engage: {}, open: null, timers: [], lastSearch: "" };

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`);

function store(kind, key) {
  try {
    return kind === "local" ? localStorage.getItem(key) : sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function save(kind, key, v) {
  try {
    (kind === "local" ? localStorage : sessionStorage).setItem(key, v);
  } catch {}
}

function visitorId() {
  let v = store("local", "wt_vid");
  if (!v) {
    v = uid();
    save("local", "wt_vid", v);
  }
  return v;
}

function sessionId() {
  const now = Date.now();
  let s = null;
  try {
    s = JSON.parse(store("local", "wt_sess") || "null");
  } catch {}
  if (!s || now - s.last > SESSION_GAP) s = { id: uid(), last: now };
  s.last = now;
  save("local", "wt_sess", JSON.stringify(s));
  return s.id;
}

function flush(beacon = false) {
  if (!S.queue.length) return;
  const body = JSON.stringify({ vid: visitorId(), sid: sessionId(), events: S.queue.splice(0, 60) });
  if (beacon && navigator.sendBeacon) navigator.sendBeacon(URL_TRACK, new Blob([body], { type: "application/json" }));
  else fetch(URL_TRACK, { method: "POST", body, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(() => {});
  if (S.queue.length) flush(beacon);
}

function push(type, data = {}) {
  if (!S.on) return;
  S.queue.push({ id: uid(), type, t: Date.now(), lang: S.lang, ...data });
  if (S.queue.length >= 20) flush();
}

function flushEngage() {
  Object.entries(S.engage).forEach(([cat, ms]) => {
    if (ms >= 1000) push("engage", { dur_ms: ms, ...(cat !== "_" ? { category_id: cat } : {}) });
  });
  S.engage = {};
}

const isActive = () => document.visibilityState === "visible" && Date.now() - S.lastInput < IDLE_MS;
const onInput = () => {
  S.lastInput = Date.now();
};
const onHide = () => {
  if (document.visibilityState === "hidden") {
    flushEngage();
    flush(true);
  }
};

function attach() {
  ["pointerdown", "keydown", "scroll", "touchstart", "wheel"].forEach((ev) => window.addEventListener(ev, onInput, { passive: true }));
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", onHide);
  S.timers.push(setInterval(() => {
    if (!isActive()) return;
    const k = S.activeCat || "_";
    S.engage[k] = (S.engage[k] || 0) + 1000;
    if (S.open) S.open.ms += 1000;
  }, 1000));
  S.timers.push(setInterval(() => {
    flushEngage();
    flush();
  }, 15000));
}

export const analytics = {
  start(lang) {
    if (S.on) return;
    S.on = true;
    S.lang = lang;
    if (S.visited) return void attach();
    S.visited = true;
    const params = new URLSearchParams(window.location.search);
    const fromQr = params.get("src") === "qr";
    const ref = document.referrer && !document.referrer.startsWith(window.location.origin);
    push("visit", { source: fromQr ? "qr" : ref ? "referral" : "direct" });
    if (fromQr) {
      params.delete("src");
      window.history.replaceState(null, "", `${window.location.pathname}${params.toString() ? `?${params}` : ""}${window.location.hash}`);
    }
    attach();
  },
  stop() {
    flushEngage();
    flush(true);
    S.timers.forEach(clearInterval);
    S.timers = [];
    S.on = false;
  },
  setLang(l) {
    S.lang = l;
  },
  setActiveCategory(id) {
    if (S.activeCat !== id) flushEngage();
    S.activeCat = id;
  },
  langSwitch(to) {
    S.lang = to;
    push("lang_switch");
  },
  impression(itemId, catId) {
    const k = `i:${itemId}`;
    if (S.seen.has(k)) return;
    S.seen.add(k);
    push("impression", { item_id: itemId, category_id: catId });
  },
  categoryView(catId) {
    if (S.cats.has(catId)) return;
    S.cats.add(catId);
    push("category_view", { category_id: catId });
  },
  categoryClick(catId) {
    push("category_click", { category_id: catId });
  },
  open(itemId, catId) {
    if (S.open) analytics.close();
    S.open = { itemId, catId, ms: 0 };
    push("item_open", { item_id: itemId, category_id: catId });
  },
  close() {
    if (!S.open) return;
    push("item_close", { item_id: S.open.itemId, category_id: S.open.catId, dur_ms: S.open.ms });
    S.open = null;
  },
  search(q, results) {
    const v = q.trim().toLowerCase();
    if (v.length < 2 || v === S.lastSearch) return;
    S.lastSearch = v;
    push("search", { q: v, results });
  },
  scroll(pct) {
    const step = [100, 75, 50, 25].find((d) => pct >= d);
    if (!step || step <= S.depth) return;
    S.depth = step;
    push("scroll", { depth: step });
  },
  click(zone) {
    push("click", { zone });
  },
  menuSelect(menuId) {
    push("menu_select", { menu_id: menuId });
    flush();
  },
};

const ZONES = [["menu-choice", "menu_choice"], ["back-to-menus", "back"], ["menu-card", "dish_card"], ["category-tab", "category_nav"], ["modal-lang-switch", "language"], ["lang-switch", "language"], ["search", "search"], ["item-modal", "dish_details"], ["hero", "hero"], ["brand", "header"], ["announcement", "announcement"], ["footer", "footer"]];

export function clickZone(target) {
  const el = target.closest?.("[data-testid]");
  const id = el?.dataset.testid || "";
  return (ZONES.find(([p]) => id.startsWith(p)) || [null, "other"])[1];
}
